import {
  Box,
  Flex,
  Heading,
  Text,
  Spinner,
  Center,
  Switch,
} from "@chakra-ui/react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { ToolsService, type ToolPublic } from "@/client"
import { FiTool } from "react-icons/fi"
import * as FaIcons from "react-icons/fa"
import { useEffect, useState } from "react"
import ToolDetail from "@/components/Tools/toolDetail"
import React from "react"

export default function ToolCardsSection() {
  const { data: tools, isLoading } = useQuery<ToolPublic[]>({
    queryKey: ["tools"],
    queryFn: () => ToolsService.getAllTools().then((res) => res.data),
  })

  const [selectedTool, setSelectedTool] = useState<ToolPublic | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const [toolCheckedMap, setToolCheckedMap] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (tools) {
      const initialMap: Record<string, boolean> = {}
      tools.forEach((tool) => {
        initialMap[tool.id] = tool.is_active // ✅ All checked by default
      })
      setToolCheckedMap(initialMap)
    }
  }, [tools])

  const toggleMutation = useMutation({
    mutationFn: ({ toolId, is_active }: { toolId: string; is_active: boolean }) =>
      ToolsService.updateToolActiveStatus({ toolId, requestBody: { is_active } }),
    onError: (err) => console.error("Activation toggle failed:", err),
  })

  const openDialog = (tool: ToolPublic) => {
    setSelectedTool(tool)
    setIsDialogOpen(true)
  }

  const closeDialog = () => {
    setIsDialogOpen(false)
    setSelectedTool(null)
  }

  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner />
      </Center>
    )
  }

  return (
    <Box px={6} py={4}>
      <Flex wrap="wrap" gap={6}>
        {tools?.map((tool) => (
          <Box
            key={tool.id}
            borderWidth="1px"
            borderRadius="2xl"
            p={5}
            w="300px"
            shadow="md"
            _hover={{ shadow: "lg" }}
            cursor="pointer"
            onClick={() => openDialog(tool)}
          >
            <Flex direction="column" justify="space-between" height="100%">
              <Box>
                <Flex align="center" mb={2} gap={2}>
                  {
                    FaIcons[tool.icon as keyof typeof FaIcons]
                      ? React.createElement(FaIcons[tool.icon as keyof typeof FaIcons])
                      : <FiTool />
                  }
                  <Heading size="md">{tool.name}</Heading>
                </Flex>
                <Text fontSize="sm" color="gray.600" mb={3} lineClamp="2">
                  {tool.description}
                </Text>
              </Box>
          
              <Box alignSelf="flex-end" mt={4} onClick={(e) => e.stopPropagation()}>
                <Switch.Root
                  checked={toolCheckedMap[tool.id] ?? true}
                  onCheckedChange={(e) => {
                    const next = e.checked
                    setToolCheckedMap((prev) => ({
                      ...prev,
                      [tool.id]: next,
                    }))
                    toggleMutation.mutate({ toolId: tool.id, is_active: next })
                  }}
                >
                  <Switch.HiddenInput />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                  <Switch.Label>
                    {toolCheckedMap[tool.id] ? "사용중" : "사용안함"}
                  </Switch.Label>
                </Switch.Root>
              </Box>
            </Flex>
          </Box>
        ))}
      </Flex>

      <ToolDetail
        isOpen={isDialogOpen}
        onClose={closeDialog}
        toolId={selectedTool?.id || null}
      />
    </Box>
  )
}
